import AdminUserApi from "@/backend/AdminUserApi";
import ConversationApi from "@/backend/ConversationApi";
import CourseApi from "@/backend/CourseApi";
import DocumentApi from "@/backend/DocumentApi";
import EvaluationsApi from "@/backend/EvaluationsApi";
import HomepageApi from "@/backend/HomepageApi";
import PaymentsApi from "@/backend/PaymentsApi";
import RegistrationsApi from "@/backend/RegistrationsApi";
import StudentTestApi from "@/backend/StudentTestApi";
import TestApi from "@/backend/TestApi";
import UploadedDocumentApi from "@/backend/UploadedDocumentApi";
import VideoApi from "@/backend/VideoApi";
import YearsApi from "@/backend/YearsApi";
import ColorHash from "color-hash";
import {
  filter,
  find,
  flatten,
  includes,
  isNil,
  map,
  omitBy,
  reverse,
  sortBy,
  uniq,
  uniqBy,
} from "lodash";
import Vue from "vue";
import Vuex from "vuex";
import { getField, updateField } from "vuex-map-fields";
import EventApi from "../backend/EventApi";
import authentication from "./authentication";
import { assignHelper } from "./helpers";

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    alert: {
      show: false,
    },
    courses: [],
    documents: [],
    payments: [],
    registrations: [],
    evaluations: {},
    progression: {},
    semester: undefined,
    filters: {
      documents: {
        kind: undefined,
        subject: undefined,
        folder: undefined,
      },
      tests: {
        subjectId: undefined,
      },
    },
    years: [],
    uploadedDocuments: [],
    plannings: [],
    homepages: [],
    facultyMarks: {
      marks: [],
      my_mark: undefined,
    },
    tests: {
      tests: [],
      pages: 0,
    },
    concoursBlancs: {
      tests: [],
      pages: 0,
    },
    currentTest: {
      questions: [],
    },
    currentTestCorrections: [],
    studentTest: {
      answer_ids: [],
      student_test_written_answers: [],
    },
    conversations: [],
    currentConversation: {
      admin_user: {},
      student: {},
    },
    adminUsers: [],
    documentSubjects: [],
    testsSubjects: [],
    videos: [],
    currentVideo: {},
    events: [],
  },
  mutations: {
    updateField,
    setAlert: assignHelper("alert"),
    setDocuments: (state, documents) => {
      state.documents = uniqBy(state.documents.concat(documents), "id");
    },
    setAdminUsers: assignHelper("adminUsers"),
    setConversations: assignHelper("conversations"),
    setCourses: assignHelper("courses"),
    setCurrentConversation: assignHelper("currentConversation"),
    setCurrentTest: assignHelper("currentTest"),
    setCurrentTestCorrections: assignHelper("currentTestCorrections"),
    setCurrentVideo: assignHelper("currentVideo"),
    setDocumentSubjects: assignHelper("documentSubjects"),
    setEvaluations: assignHelper("evaluations"),
    setFacultyMarks: assignHelper("facultyMarks"),
    setHomepages: assignHelper("homepages"),
    setPayments: assignHelper("payments"),
    setPlannings: assignHelper("plannings"),
    setProgression: assignHelper("progression"),
    setRegistrations: assignHelper("registrations"),
    setSemester: assignHelper("semester"),
    setStudentTest: assignHelper("studentTest"),
    setTests: assignHelper("tests"),
    setTestsSubjects: assignHelper("testsSubjects"),
    setConcoursBlancs: assignHelper("concoursBlancs"),
    setUploadedDocuments: assignHelper("uploadedDocuments"),
    setVideos: assignHelper("videos"),
    setYears: assignHelper("years"),
    setEvents: assignHelper("events"),
    updateEvent(state, updatedEvent) {
      const index = state.events.findIndex((e) => e.id === updatedEvent.id);
      if (index !== -1) {
        state.events.splice(index, 1, updatedEvent);
      }
    },
  },
  actions: {
    fetchCourses({ commit }) {
      return CourseApi.list().then((response) => {
        commit("setCourses", response.data);
      });
    },
    fetchDocuments({ commit }, payload) {
      const { page, kind, subject } = payload;
      DocumentApi.list(page, kind, subject).then((response) => {
        if (response.data.length > 0) {
          commit("setDocuments", response.data);
        } else {
          commit("setAlert", {
            text: "Aucun autre document disponible",
            show: true,
            color: "error",
          });
        }
      });
    },
    fetchDocumentSubjects({ commit }) {
      DocumentApi.subjects().then((response) => {
        commit("setDocumentSubjects", response.data);
      });
    },
    fetchDocument(_, documentId) {
      return DocumentApi.get(documentId);
    },
    fetchPayments({ commit }) {
      PaymentsApi.list().then((response) => {
        commit("setPayments", response.data);
      });
    },
    fetchRegistrations({ commit }) {
      RegistrationsApi.list().then((response) => {
        commit("setRegistrations", response.data);
      });
    },
    fetchEvaluations({ commit }, semester = null) {
      commit("setEvaluations", []);
      return EvaluationsApi.list(semester).then((response) => {
        commit("setEvaluations", response.data);
      });
    },
    fetchProgression({ commit }) {
      return EvaluationsApi.progression().then((response) => {
        commit("setProgression", response.data);
      });
    },
    fetchFacultyMarks({ commit }, semester) {
      return EvaluationsApi.facultyMarks(semester).then((response) => {
        commit("setFacultyMarks", response.data);
      });
    },
    fetchYears({ commit }) {
      return YearsApi.list().then((response) => {
        commit("setYears", response.data);
      });
    },
    fetchUploadedDocuments({ commit }) {
      return UploadedDocumentApi.list().then((response) => {
        commit("setUploadedDocuments", response.data);
      });
    },
    sendDocuments({ commit }, documents) {
      return UploadedDocumentApi.upload(documents).then((response) => {
        commit("setUploadedDocuments", response.data);
      });
    },
    fetchPlannings({ commit }) {
      return DocumentApi.listPlannings().then((response) => {
        commit("setPlannings", response.data);
      });
    },
    fetchHomepages({ commit }) {
      return HomepageApi.list().then((response) => {
        commit("setHomepages", response.data);
      });
    },
    fetchTests({ commit }, { page, subjectId }) {
      return TestApi.list(page, subjectId).then((response) => {
        commit("setTests", response.data);
      });
    },
    fetchConcoursBlancs({ commit }, { page, subjectId }) {
      return TestApi.listWhiteTests(page, subjectId).then((response) => {
        commit("setConcoursBlancs", response.data);
      });
    },
    fetchTestsSubjects({ commit }) {
      return TestApi.subjects().then((response) => {
        commit("setTestsSubjects", response.data);
      });
    },
    fetchCurrentTest({ commit }, testId) {
      return TestApi.show(testId).then((response) => {
        commit("setCurrentTest", response.data);
        const studentTest = response.data.student_test || { student_test_written_answers: [] };
        const answeredQuestionIds = studentTest.student_test_written_answers.map(
          (answer) => answer.answer.length > 0
        );
        const missingStudentTestWrittenAnswers = response.data.questions.filter(
          (question) => question.written_answer && !answeredQuestionIds.includes(question.id)
        );
        studentTest.student_test_written_answers = studentTest.student_test_written_answers.concat(
          missingStudentTestWrittenAnswers.map((question) => ({
            question_id: question.id,
            answer: "",
          }))
        );
        commit("setStudentTest", studentTest);
      });
    },
    newStudentTest({ commit, dispatch }) {
      commit("setStudentTest", { answer_ids: [], student_test_written_answers: [] });
      return dispatch("updateStudentTest");
    },
    updateStudentTest({ state, commit }) {
      const latestAnswersMap = {};
      state.studentTest.student_test_written_answers.forEach((answer) => {
        latestAnswersMap[answer.question_id] = answer;
      });
      state.studentTest.student_test_written_answers = Object.values(latestAnswersMap);
      if (state.studentTest.id) {
        return StudentTestApi.update(
          state.studentTest.id,
          state.studentTest.answer_ids,
          state.studentTest.student_test_written_answers
        ).then((response) => {
          commit("setStudentTest", response.data);
        });
      }
      return StudentTestApi.create(
        state.currentTest.id,
        state.studentTest.answer_ids,
        state.studentTest.student_test_written_answers
      ).then((response) => {
        commit("setStudentTest", response.data);
      });
    },
    finishPendingStudentTest(_, testId) {
      return StudentTestApi.finish(testId);
    },
    fetchTestCorrections({ commit }, testId) {
      return TestApi.correction(testId).then((response) => {
        commit("setCurrentTestCorrections", response.data);
      });
    },
    fetchConversations({ commit }) {
      return ConversationApi.list().then((response) => {
        commit("setConversations", response.data);
      });
    },
    fetchCurrentConversation({ commit }, id) {
      return ConversationApi.get(id).then((response) => {
        commit("setCurrentConversation", response.data);
      });
    },
    fetchVideos({ commit }) {
      return VideoApi.list().then((response) => {
        commit("setVideos", response.data);
      });
    },
    fetchCurrentVideo({ commit }, videoId) {
      return VideoApi.show(videoId).then((response) => {
        commit("setCurrentVideo", response.data);
      });
    },
    postMessage({ commit, state }, { message, adminUserId, files = [] }) {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append("files[]", file);
      });
      formData.append("body", message);

      if (state.currentConversation.id) {
        return ConversationApi.postMessage(state.currentConversation.id, formData).then(
          (response) => {
            commit("setCurrentConversation", response.data);
            return response;
          }
        );
      }

      return ConversationApi.create(adminUserId, formData).then((response) => {
        commit("setCurrentConversation", response.data);
        return response;
      });
    },
    fetchAdminUsers({ commit }) {
      return AdminUserApi.list().then((response) => {
        commit("setAdminUsers", response.data);
      });
    },
    fetchEvents({ commit }) {
      return EventApi.list().then((response) => {
        commit("setEvents", response.data);
      });
    },
    markEventAsSeen({ commit }, eventId) {
      return EventApi.markEventAsSeen(eventId).then((response) => {
        commit("updateEvent", response.data);
      });
    },
  },
  modules: {
    authentication,
  },
  getters: {
    getField,
    sortedConversationMessages({ currentConversation }) {
      const { messages } = currentConversation;
      return sortBy(messages, "created_at");
    },
    facultiesByYear({ years }) {
      return (sessionId) => {
        const selectedYear = find(years, { id: sessionId });
        if (selectedYear) {
          return filter(selectedYear.faculties, (faculty) => faculty.name !== "Annulation");
        }
        return [];
      };
    },
    filteredDocuments({ documents, filters }) {
      const relevantFilters = omitBy(filters.documents, isNil);
      let filteredDocuments = documents;
      if (filters.documents.folder) {
        filteredDocuments = filter(documents, (document) =>
          includes(map(document.folders, "name"), filters.documents.folder.name)
        );
        delete relevantFilters.folder;
      }
      if (filters.documents.subject) {
        filteredDocuments = filter(
          documents,
          (document) => document.subject && document.subject.name === filters.documents.subject
        );
        delete relevantFilters.subject;
      }
      return filter(filteredDocuments, relevantFilters);
    },
    documentFolders({ documents }) {
      const folders = uniqBy(flatten(documents.map((document) => document.folders)), "name");

      return map(folders, (folder) => ({
        text: folder.name,
        value: folder,
      }));
    },
    sortedPayments({ payments }) {
      return reverse(sortBy(payments, "cashed_at"));
    },
    availableSemesters({ evaluations }) {
      return uniq(evaluations.evaluations.map((evaluation) => evaluation.exam.semester));
    },
    lastSemester({ progression }) {
      if (progression.semesters) {
        return progression.semesters[Object.keys(progression.semesters).pop()];
      }
      return { min_and_max: { max: undefined, min: undefined } };
    },
    generalProgressionChart({ progression }) {
      if (progression.semesters) {
        return {
          labels: map(progression.semesters, "semester"),
          dataSets: [
            {
              label: "Moyenne générale",
              data: map(progression.semesters, "average_mark"),
              borderColor: "#003360",
              backgroundColor: "#003360",
              pointBackgroundColor: "#003360",
              pointBorderWidth: 3,
              fill: false,
              lineTension: 0,
            },
          ],
        };
      }
      return {
        labels: [],
        dataSets: [],
      };
    },
    subjectProgressionChart({ progression }) {
      if (progression.semesters) {
        const colorHash = new ColorHash();
        let dates = map(sortBy(progression.evaluations, "exam.date"), (evaluation) =>
          new Date(evaluation.exam.date).toLocaleDateString("fr")
        );
        dates = uniq(dates);

        const subjects = uniq(map(progression.evaluations, "exam.subject.name"));

        const dataSets = map(subjects, (subject) => {
          const filteredEvaluations = filter(
            progression.evaluations,
            (evaluation) => evaluation.exam.subject.name === subject
          );

          return {
            label: subject,
            fill: false,
            lineTension: 0,
            backgroundColor: colorHash.hex(subject),
            borderColor: colorHash.hex(subject),
            pointBackgroundColor: colorHash.hex(subject),
            pointBorderWidth: 3,
            data: map(filteredEvaluations, (evaluation) => ({
              x: new Date(evaluation.exam.date).toLocaleDateString(),
              y: evaluation.mark,
            })),
          };
        });

        return {
          labels: dates,
          dataSets,
        };
      }
      return {
        labels: [],
        dataSets: [],
      };
    },
  },
});
